import React, { useContext } from 'react';
import { Connecting, IceCreamImage } from 'src/assets/images';
import { LoaderAnimation } from 'src/components';
import { ApplicationActionType, Button } from 'src/features/common';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import { FICA_STATUS } from 'src/features/common/types/salesforce/api/application/salesforce-response';
import { ApplicationContext } from 'src/features/onboarding/context';

const POLLING_DELAY_SEC = 30;
const MAX_FICA_POLLING_DURATION_SEC = 60 * 10;
const keys = {
  ficaPollingStartTime: 'fica_polling_start_time',
};

type Props = {
  setFicaSuccessful: (status: boolean) => void;
};

export function OnboardingFica({ setFicaSuccessful }: Props) {
  const [ficaStatus, setFicaStatus] = React.useState<FICA_STATUS>(FICA_STATUS.inProgress);
  const { application, salesforceService, dispatch } = useContext(ApplicationContext);

  const getFicaPollingStartTime = () => {
    const value = sessionStorage.getItem(keys.ficaPollingStartTime);
    return !value ? value : Number(sessionStorage.getItem(keys.ficaPollingStartTime));
  };

  const initFicaPollingStartTime = () => {
    if (!getFicaPollingStartTime()) {
      const now = Date.now();
      sessionStorage.setItem(keys.ficaPollingStartTime, JSON.stringify(now));
    }
  };

  const screenContent: {
    [key: string]: {
      graphic: React.ReactNode;
      style: string;
      heading: string;
      text: string;
    };
  } = {
    inProgress: {
      graphic: <LoaderAnimation height={125} width={125} />,
      style: 'in-progress',
      heading: `Thank you for your application ${application.contact?.name ?? ''}`,
      text: 'We are processing the FICA application, we will give you feedback within + - 5 minutes.',
    },
    Failed: {
      graphic: <img src={IceCreamImage} />,
      style: 'unsuccessful',
      heading: 'Application unsuccessful',
      text: "If you'd like assistance please contact the call centre.",
    },
    pending: {
      graphic: <img src={Connecting} />,
      style: 'pending',
      heading: 'FICA Pending',
      text:
        'We have referred the application for further review, please ' +
        'return to the store at a later stage to complete your application.',
    },
  };

  React.useEffect(() => {
    if (ficaStatus === FICA_STATUS.inProgress) {
      initFicaPollingStartTime();

      const interval = setInterval(async () => {
        if (ficaStatus === FICA_STATUS.inProgress) {
          const status = await salesforceService.refreshFicaStatus(
            application?.referenceNumber || '',
          );
          setFicaSuccessful(status === FICA_STATUS.passed);
          setFicaStatus(status || FICA_STATUS.inProgress);
          const ficaPollingStartTime = getFicaPollingStartTime() as number;
          if (
            ficaPollingStartTime &&
            (Date.now() - ficaPollingStartTime) / 1000 >= MAX_FICA_POLLING_DURATION_SEC
          ) {
            setFicaStatus(FICA_STATUS.pending);
          }
        } else {
          clearInterval(interval);
        }
      }, POLLING_DELAY_SEC * 1000);
      return () => clearInterval(interval);
    }
  }, []);

  const content = screenContent[ficaStatus];

  const onClick = () => {
    dispatch({
      type: ApplicationActionType.RESTART,
    });
  };

  return (
    <article className="flex flex-col items-center gap-12">
      <section>{content.graphic}</section>

      <h1 className="text-4xl font-medium text-grey-750">{content.heading}</h1>

      <div className="text-center max-w-[600px]">
        <p className="mb-2 text-xl">{content.text}</p>

        <p className="mb-12 text-xl">
          <span className="font-bold">Order reference</span>: {application.referenceNumber}
        </p>

        <Button
          type={'submit'}
          buttonStyle={DefaultButtonStyle()
            .hasPrimaryAction()
            .isMediumSize()
            .hasLightMode()
            .build()}
          buttonParams={{
            type: ButtonParamType.NormalButton,
            onClick,
          }}
          className="w-56"
          automationid={`DA_Continue_Button`}
        >
          Close
        </Button>
      </div>
    </article>
  );
}
